import download from 'downloadjs'
import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, interval, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators'

import { openInNewTab } from 'utils/DOM'
import { URL_CONST } from 'utils/urlConsts'

import { GET_BILLING_STATUS_ASYNC } from 'containers/Agents/constants'
import { MINIMAL_ALLOWED_POLLING_INTERVAL } from 'containers/Polling/constants'
import { addToast } from 'containers/Toasts/actions'

import { generateSourceData } from 'components/pages/resolutionCenter/Report/utils/analysisExtractors'

import { mobileSwitcher } from '../actions'
import { INCIDENT_TYPE, MAP_INCIDENT_TYPE_TO_URL_PREFIX } from '../constants'
import { openChangedStateModal } from '../ReportCommunicationModal/actions'

import {
  changeAssigneesError,
  changeAssigneesSuccess,
  changeReportStateError,
  changeReportStateSuccess,
  closeIncidentError,
  closeIncidentSuccess,
  closeSafeChallengeError,
  closeSafeChallengeSuccess,
  downloadOriginalEmailError,
  downloadOriginalEmailSuccess,
  fetchAffMailboxesCountsError,
  fetchAffMailboxesCountsSuccess,
  fetchAssigneesError,
  fetchAssigneesSuccess,
  fetchCommunityInsightsError,
  fetchCommunityInsightsSuccess,
  fetchElementsError,
  fetchElementsSuccess,
  fetchEmailSnapshotError,
  fetchEmailSnapshotSuccess,
  fetchMSFTQuarantineReportError,
  fetchMSFTQuarantineReportSuccess,
  fetchPaginatedReportersError,
  fetchPaginatedReportersSuccess,
  fetchReportElementsError,
  fetchReportElementsSuccess,
  fetchReportError,
  fetchReportSuccess,
  fetchScansFreemiumLimitError,
  fetchScansFreemiumLimitSuccess,
  keepEncryptedIncidentInQuarantineError,
  keepEncryptedIncidentInQuarantineSuccess,
  releaseEncryptedIncidentError,
  releaseEncryptedIncidentSuccess,
  releaseMSFTQuarantineReportError,
  releaseMSFTQuarantineReportSuccess,
  themisAnalysisFailure,
  themisAnalysisPartialUpdate,
  themisAnalysisSuccess,
  updateElementsError,
  updateElementsSuccess,
  updateMSFTQuarantineAssigneeError,
  updateMSFTQuarantineAssigneeSuccess,
  validateNewAffectedMailboxesSeenError,
  validateNewAffectedMailboxesSeenSuccess,
} from './actions'
import {
  CHANGE_ASSIGNEES_ASYNC,
  CLOSE_INCIDENT_ASYNC,
  CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC,
  DOWNLOAD_ORIGINAL_EMAIL_ASYNC,
  EMAIL_SNAPSHOT_ASYNC,
  FETCH_AFFECTED_MAILBOXES_COUNTS_ASYNC,
  FETCH_ASSIGNEES_ASYNC,
  FETCH_COMMUNITY_INSIGHTS_ASYNC,
  FETCH_MSFT_QUARANTINE_REPORT_ASYNC,
  FETCH_PAGINATED_REPORTERS_ASYNC,
  FETCH_REPORT_ELEMENTS_ASYNC,
  FETCH_SCANS_FREEMIUM_LIMIT_ASYNC,
  KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC,
  MSFT_QUARANTINE_ACTIONS,
  OPEN_EMAIL_SNAPSHOT_NEW_TAB,
  RELEASE_ENCRYPTED_INCIDENT_ASYNC,
  RELEASE_MSFT_QUARANTINE_REPORT_ASYNC,
  REPORT_ELEMENTS_REQUEST_ASYNC,
  REPORT_ELEMENTS_UPDATE_ASYNC,
  REPORT_REQUEST_ASYNC,
  REPORT_STATE_CHANGE_ASYNC,
  THEMIS_ANALYSIS_ASYNC,
  UPDATE_MSFT_QUARANTINE_ASSIGNEE_ASYNC,
  VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC,
} from './constants'

const fetchReportEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REPORT_REQUEST_ASYNC.INITIATE, REPORT_STATE_CHANGE_ASYNC.SUCCESS),
    switchMap((action) =>
      api.resolutionCenter
        .getReport(
          action.payload.reportId,
          MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
        )
        .pipe(
          map((response) =>
            fetchReportSuccess({
              report: {
                ...response.report,
                reportType: action.payload.reportType,
                reportId: action.payload.reportId,
              },
            })
          ),
          catchError((error) => of(epicsErrorHandler(fetchReportError, error, [404]))),
          takeUntil(action$.pipe(ofType(REPORT_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const fetchCommunityInsightsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_COMMUNITY_INSIGHTS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.getCommunityInsights(action.payload.reportId).pipe(
        map((response) => fetchCommunityInsightsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchCommunityInsightsError, error, [404]))),
        takeUntil(action$.pipe(ofType(FETCH_COMMUNITY_INSIGHTS_ASYNC.CANCEL)))
      )
    )
  )

const fetchAssigneesEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_ASSIGNEES_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .getAssignees(state$.value.partnerOptions.selectedCompany?.value, action.payload.search)
        .pipe(
          map((response) => fetchAssigneesSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchAssigneesError, error))),
          takeUntil(action$.pipe(ofType(FETCH_ASSIGNEES_ASYNC.CANCEL)))
        )
    )
  )

const changeAssigneeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CHANGE_ASSIGNEES_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .changeAssignee(
          action.payload.reportId,
          { assignee_id: action.payload.assigneeId },
          MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
        )
        .pipe(
          map((response) => changeAssigneesSuccess(response)),
          catchError((error) => of(epicsErrorHandler(changeAssigneesError, error))),
          takeUntil(action$.pipe(ofType(CHANGE_ASSIGNEES_ASYNC.CANCEL)))
        )
    )
  )

const changeReportStateEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REPORT_STATE_CHANGE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .changeReportState(
          action.payload.reportId,
          action.payload.newState,
          MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
        )
        .pipe(
          mergeMap((response) => {
            mobileSwitcher.showList()
            return of(
              changeReportStateSuccess(
                response,
                action.payload.newState,
                action.payload.reportType,
                action.payload.reportId
              ),
              action.payload.newState !== 'delete' &&
                openChangedStateModal({
                  reportId: action.payload.reportId,
                  reportType: action.payload.reportType,
                  newState: action.payload.newState,
                  isFederation: response.response?.reportIsFederation,
                  allowTags: response.response?.allowTags,
                  communityUserExists: response.response?.communityUserExists,
                  mitigationsCount: response.response?.mitigationsCount,
                  classificationTexts: response.response?.classificationTexts,
                })
            )
          }),
          catchError((error) => of(epicsErrorHandler(changeReportStateError, error))),
          takeUntil(action$.pipe(ofType(REPORT_STATE_CHANGE_ASYNC.CANCEL)))
        )
    )
  )

const updateElementsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REPORT_ELEMENTS_UPDATE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .updateElements(
          action.payload.reportId,
          action.payload.elementsName,
          MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
        )
        .pipe(
          map((response) => updateElementsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(updateElementsError, error))),
          takeUntil(action$.pipe(ofType(REPORT_ELEMENTS_UPDATE_ASYNC.CANCEL)))
        )
    )
  )

const fetchElementsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(REPORT_ELEMENTS_REQUEST_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter
        .fetchElements(
          action.payload.reportId,
          action.payload.elementsName,
          MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
        )
        .pipe(
          map((response) =>
            fetchElementsSuccess(response, action.payload.reportId, action.payload.elementsName)
          ),
          catchError((error) => of(epicsErrorHandler(fetchElementsError, error))),
          takeUntil(action$.pipe(ofType(REPORT_ELEMENTS_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const validateNewAffectedMailboxesSeen = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC.INITIATE),
    mergeMap(() =>
      api.resolutionCenter.validateNewAffectedMailboxesSeen().pipe(
        map((response) => validateNewAffectedMailboxesSeenSuccess(response)),
        catchError((error) => of(epicsErrorHandler(validateNewAffectedMailboxesSeenError, error))),
        takeUntil(action$.pipe(ofType(VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC.CANCEL)))
      )
    )
  )

const fetchAffMailboxesCountsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_AFFECTED_MAILBOXES_COUNTS_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.fetchAffMailboxesCounts(action.payload.reportId).pipe(
        map((response) => fetchAffMailboxesCountsSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchAffMailboxesCountsError, error)))
      )
    )
  )

const fetchScansFreemiumLimitEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_SCANS_FREEMIUM_LIMIT_ASYNC.INITIATE),
    mergeMap(() =>
      api.resolutionCenter.fetchScansFreemiumLimit().pipe(
        map((response) => fetchScansFreemiumLimitSuccess(response)),
        catchError((error) => of(epicsErrorHandler(fetchScansFreemiumLimitError, error)))
      )
    )
  )

const downloadOriginalEmailEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DOWNLOAD_ORIGINAL_EMAIL_ASYNC.INITIATE),
    mergeMap((action) => {
      const { reportId, emailType } = action.payload

      if (emailType === 'original_eml') {
        return api.resolutionCenter.downloadOriginalEml(reportId).pipe(
          map((response) => {
            download(response, `${reportId}.eml`, 'message/rfc822')
            return downloadOriginalEmailSuccess(response)
          }),
          catchError((error) => {
            const errorMessage = error?.response?.error || 'Email cannot be downloaded'
            return of(
              epicsErrorHandler(downloadOriginalEmailError, error, [400, 403, 404]),
              addToast({
                message: errorMessage,
                type: 'error',
                id: 'download_original_email_error',
              })
            )
          })
        )
      }

      return api.resolutionCenter.fetchRawEmail(reportId, emailType).pipe(
        map((response) => {
          download(response, 'body-raw.html')
          return downloadOriginalEmailSuccess(response)
        }),
        catchError((error) => of(epicsErrorHandler(downloadOriginalEmailError, error)))
      )
    })
  )

const closeIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CLOSE_INCIDENT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.closeIncident(action.payload.reportId).pipe(
        map((response) => closeIncidentSuccess(response)),
        catchError((error) => of(epicsErrorHandler(closeIncidentError, error, [403])))
      )
    )
  )

const closeSafeChallengeIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.closeSafeChallengeIncident(action.payload.reportId).pipe(
        map((response) => closeSafeChallengeSuccess(response)),
        catchError((error) => of(epicsErrorHandler(closeSafeChallengeError, error)))
      )
    )
  )

const emailSnapshotEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(EMAIL_SNAPSHOT_ASYNC.INITIATE, OPEN_EMAIL_SNAPSHOT_NEW_TAB),
    mergeMap((action) =>
      api.resolutionCenter.getEmailSnapshot(action.payload.reportId).pipe(
        map((response) => {
          if (action.type === OPEN_EMAIL_SNAPSHOT_NEW_TAB) {
            openInNewTab(response.emailBodyPreviewUrl)
          }
          return fetchEmailSnapshotSuccess(response)
        }),
        catchError((error) => of(epicsErrorHandler(fetchEmailSnapshotError, error, [403, 404])))
      )
    )
  )

const releaseEncryptedIncidentEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_ENCRYPTED_INCIDENT_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.releaseEncryptedIncident(action.payload.reportId).pipe(
        mergeMap((response) =>
          of(
            releaseEncryptedIncidentSuccess(response),
            addToast({ message: 'The email has been released to the end user inbox.' })
          )
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(releaseEncryptedIncidentError, error),
            addToast({ message: error.response?.message || error.response?.detail, type: 'error' })
          )
        )
      )
    )
  )

const keepEncryptedIncidentInQuarantine = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC.INITIATE),
    mergeMap((action) =>
      api.resolutionCenter.keepEncryptedIncidentInQuarantine(action.payload.reportId).pipe(
        map((response) => keepEncryptedIncidentInQuarantineSuccess(response)),
        catchError((error) =>
          of(epicsErrorHandler(keepEncryptedIncidentInQuarantineError, error, [403, 404]))
        )
      )
    )
  )

const fetchReportElementsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_REPORT_ELEMENTS_ASYNC.INITIATE),
    mergeMap((action) => {
      const { reportId, body, reportType } = action.payload
      const { otype, page } = body
      // Add is_potential flag for potential mail reports
      const isPotential =
        reportType === INCIDENT_TYPE.POTENTIAL_MAIL ||
        reportType === INCIDENT_TYPE.AUTO_THEMIS_POTENTIAL_MAIL
      const requestBody = isPotential ? { ...body, is_potential: true } : body
      return api.resolutionCenter.getReportElements(reportId, requestBody).pipe(
        map(({ response: data }) => fetchReportElementsSuccess(data, otype, page)),
        catchError((error) => of(epicsErrorHandler(fetchReportElementsError, error, [400, 404]))),
        takeUntil(action$.pipe(ofType(FETCH_REPORT_ELEMENTS_ASYNC.CANCEL)))
      )
    })
  )

const fetchPaginatedReportersEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_PAGINATED_REPORTERS_ASYNC.INITIATE),
    mergeMap((action) => {
      const { reportId, page } = action.payload
      return api.resolutionCenter.getReportReporters(reportId, page).pipe(
        map((data) => fetchPaginatedReportersSuccess(data)),
        catchError((error) =>
          of(epicsErrorHandler(fetchPaginatedReportersError, error, [400, 404]))
        ),
        takeUntil(action$.pipe(ofType(FETCH_PAGINATED_REPORTERS_ASYNC.CANCEL)))
      )
    })
  )

const fetchMSFTQuarantineReportEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_MSFT_QUARANTINE_REPORT_ASYNC.INITIATE),
    switchMap((action) =>
      api.resolutionCenter.getMSFTQuarantineReport(action.payload.reportId).pipe(
        map((response) => fetchMSFTQuarantineReportSuccess(response)),
        catchError((error) =>
          of(epicsErrorHandler(fetchMSFTQuarantineReportError, error, [403, 404]))
        )
      )
    )
  )

const releaseMSFTQuarantineReportEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(RELEASE_MSFT_QUARANTINE_REPORT_ASYNC.INITIATE),
    mergeMap((action) => {
      const { reportId, action: msftAction } = action.payload
      const isRelease = msftAction === MSFT_QUARANTINE_ACTIONS.RELEASE

      if (!isRelease) {
        return api.resolutionCenter.releaseMSFTQuarantineReport(reportId, msftAction).pipe(
          mergeMap((response) =>
            of(
              releaseMSFTQuarantineReportSuccess(response),
              addToast({ message: 'The incident has been closed.' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(releaseMSFTQuarantineReportError, error, [403]),
              addToast({
                message:
                  error.response?.message || error.response?.detail || error.response || error,
                type: 'error',
              })
            )
          )
        )
      }

      return api.resolutionCenter.releaseMSFTQuarantineReport(reportId, msftAction).pipe(
        switchMap(({ response }) => {
          const { task_id: taskId } = response || {}

          if (!taskId) {
            return of(
              releaseMSFTQuarantineReportSuccess(response),
              addToast({
                id: 'Microsoft_incident_release_Started',
                message: (
                  <span id="Microsoft_incident_release">
                    Release request sent to Defender. You’ll be notified once it’s completed
                  </span>
                ),
              })
            )
          }

          return interval(MINIMAL_ALLOWED_POLLING_INTERVAL).pipe(
            switchMap(() => api.resolutionCenter.releaseMSFTQuarantineReportStatus(taskId)),
            filter(
              (statusResponse) =>
                statusResponse.task_status === 'finished' ||
                statusResponse.task_status === 'failed' ||
                statusResponse.task_status === 'reintegration_required' ||
                statusResponse.statusCode >= 400
            ),
            take(1),
            mergeMap((statusResponse) => {
              if (statusResponse.task_status === 'finished') {
                return of(
                  addToast({
                    id: 'Microsoft_incident_release_Success',
                    message: (
                      <span id="Microsoft_incident_release">
                        The release request for <b>Incident #MSFT-{reportId}</b> was completed.{' '}
                        <span
                          tabIndex={0}
                          role="button"
                          id="Microsoft_incident_release_View_details_link"
                          onClick={() => {
                            window.open(
                              `${URL_CONST.incidents.attackdetails}/msft/${reportId}`,
                              null,
                              'noopener,noreferrer'
                            )
                          }}
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: 'white',
                          }}
                        >
                          View details
                        </span>
                      </span>
                    ),
                  }),
                  RELEASE_MSFT_QUARANTINE_REPORT_ASYNC.onSuccess({ ...statusResponse, reportId })
                )
              }

              const errorMessage =
                statusResponse.task_status === 'reintegration_required'
                  ? 'Please reintegrate Advanced Integration'
                  : statusResponse.error_message || 'Something went wrong. Please try again'

              return of(
                addToast({
                  type: 'error',
                  id: 'Microsoft_incident_release_Error',
                  message: <span id="Microsoft_incident_release">{errorMessage}</span>,
                }),
                RELEASE_MSFT_QUARANTINE_REPORT_ASYNC.onError({ ...statusResponse, reportId })
              )
            })
          )
        }),
        startWith(
          addToast({
            type: 'info',
            id: 'Microsoft_incident_release_Info',
            message: (
              <span id="Microsoft_incident_release">
                Release request sent to Defender. You’ll be notified once it’s completed
              </span>
            ),
          })
        ),
        catchError((error) =>
          of(
            epicsErrorHandler(releaseMSFTQuarantineReportError, error, [403]),
            addToast({
              message: error.response?.message || error.response?.detail || error.response || error,
              type: 'error',
            })
          )
        )
      )
    })
  )

const updateMSFTQuarantineAssigneeEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_MSFT_QUARANTINE_ASSIGNEE_ASYNC.INITIATE),
    mergeMap((action) => {
      const { reportId, assigneeId, reviewInDefender } = action.payload

      return api.resolutionCenter
        .updateMSFTQuarantineAssignee(reportId, { assigneeId, reviewInDefender })
        .pipe(
          map((response) => updateMSFTQuarantineAssigneeSuccess({ response })),
          catchError((error) => of(epicsErrorHandler(updateMSFTQuarantineAssigneeError, error)))
        )
    })
  )

const unescapeMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text
  return text
    .replaceAll(String.raw`\n`, '\n')
    .replaceAll(String.raw`\t`, '\t')
    .replaceAll(String.raw`\"`, '"')
    .replaceAll(String.raw`\'`, "'")
    .replaceAll(String.raw`\\`, '\\')
}

const findSourceEntry = (events, id) => events.findIndex((e) => e.type === 'source' && e.id === id)

const createThemisObserver = (observer, reportId, reportType, api) => {
  const toolArgsMap = new Map()
  let tableOrderCounter = 0
  let textBuffer = ''
  let activityEvents = []
  let dynamicCards = {}
  let dynamicTables = {}
  let tasks = null
  let analysisData = null
  let fieldLoadingStates = {
    sender_analysis: true,
    body_analysis: true,
    links_analysis: true,
    attachments_analysis: true,
    relays_analysis: true,
    label_explanation: true,
  }

  const controller = new AbortController()

  const appendAiMessage = (messageText) => {
    activityEvents = [
      {
        type: 'message',
        // eslint-disable-next-line no-restricted-properties -- non-sensitive UI list key, not used for security
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        text: messageText,
      },
      ...activityEvents,
    ]
    observer.next(themisAnalysisPartialUpdate(reportId, { activityEvents }))
  }

  const appendOrUpdateSource = (toolCallId, sourceData) => {
    const idx = findSourceEntry(activityEvents, toolCallId)
    const entry = { type: 'source', id: toolCallId, data: sourceData }
    if (idx >= 0) {
      activityEvents = [...activityEvents.slice(0, idx), entry, ...activityEvents.slice(idx + 1)]
    } else {
      activityEvents = [entry, ...activityEvents]
    }
    observer.next(themisAnalysisPartialUpdate(reportId, { activityEvents }))
  }

  observer.next(themisAnalysisPartialUpdate(reportId, { fieldLoadingStates }))

  const processLine = (line) => {
    if (!line.trim()) return
    try {
      const data = JSON.parse(line)

      if (data.event_type === 'text' && data.text) {
        textBuffer += data.text
        return
      }
      if (data.content_type === 'thinking' && data.text) {
        textBuffer += data.text
        return
      }

      if (data.event_type === 'tool_call_start') {
        if (textBuffer.trim()) {
          appendAiMessage(unescapeMarkdown(textBuffer))
          textBuffer = ''
        }
        const toolArgs = data.tool_args || {}
        toolArgsMap.set(data.tool_call_id, toolArgs)
        const sourceData = generateSourceData(data.tool_call_id, data.tool_name, toolArgs)
        if (sourceData) appendOrUpdateSource(data.tool_call_id, sourceData)
        observer.next(themisAnalysisPartialUpdate(reportId, { hasReceivedToolCallStart: true }))
        return
      }

      if (data.event_type === 'tool_call_result') {
        const storedArgs = toolArgsMap.get(data.tool_call_id) || {}

        if (data.tool_name === 'progress_update_tool') {
          const args = { ...storedArgs, ...data.tool_args }
          const title = args.title || 'Update'
          const message = args.message || ''
          if (title || message) {
            appendAiMessage(unescapeMarkdown(`**${title}**\n${message}`))
          }
          return
        }

        const sourceData = generateSourceData(
          data.tool_call_id,
          data.tool_name,
          storedArgs,
          data.tool_result
        )
        if (sourceData) appendOrUpdateSource(data.tool_call_id, sourceData)

        if (data.tool_name === 'create_tasks') {
          try {
            const result = JSON.parse(data.tool_result)
            if (result.success && result.current_tasks_by_id) {
              const tasksWithStatus = {}
              Object.entries(result.current_tasks_by_id).forEach(([id, task]) => {
                tasksWithStatus[id] = {
                  name: task.name,
                  description: task.description || '',
                  status: 'PENDING',
                }
              })
              tasks = tasksWithStatus
              observer.next(themisAnalysisPartialUpdate(reportId, { tasks }))
            }
          } catch {} // eslint-disable-line no-empty
        } else if (data.tool_name === 'transition_task_status') {
          try {
            const result = JSON.parse(data.tool_result)
            if (result.success && storedArgs?.task_id) {
              tasks = {
                ...tasks,
                [storedArgs.task_id]: {
                  ...tasks?.[storedArgs.task_id],
                  status: storedArgs.status,
                },
              }
              observer.next(themisAnalysisPartialUpdate(reportId, { tasks }))
            }
          } catch {} // eslint-disable-line no-empty
        } else if (data.tool_name === 'add_card') {
          try {
            let payload = {}
            if (data.tool_result) {
              try {
                payload =
                  typeof data.tool_result === 'string'
                    ? JSON.parse(data.tool_result)
                    : data.tool_result
              } catch {
                payload = { content: String(data.tool_result) }
              }
            }
            if (!payload || typeof payload !== 'object') payload = {}
            const title = payload.title || storedArgs.title || 'Insight'
            const content = payload.content || storedArgs.content || ''
            dynamicCards = {
              ...dynamicCards,
              [data.tool_call_id]: { title, content },
            }
            observer.next(themisAnalysisPartialUpdate(reportId, { dynamicCards }))
          } catch {} // eslint-disable-line no-empty
        } else if (data.tool_name === 'add_table') {
          try {
            let payload = {}
            if (data.tool_result) {
              try {
                payload =
                  typeof data.tool_result === 'string'
                    ? JSON.parse(data.tool_result)
                    : data.tool_result
              } catch {
                payload = {}
              }
            }
            if (!payload || typeof payload !== 'object') payload = {}
            const mergedPayload = { ...payload, ...storedArgs }
            const tableTitle = mergedPayload.title || 'Table'
            const maxRows = 8

            let columns = mergedPayload.columns
            if (!Array.isArray(columns) || columns.length === 0) throw new Error('Invalid columns')
            columns = columns.slice(0, 4).map(String)

            let tableData = mergedPayload.data
            if (!Array.isArray(tableData)) throw new Error('Invalid data')
            tableData = tableData.slice(0, maxRows).map((row) => {
              if (!Array.isArray(row)) return Array.from({ length: columns.length }).fill('')
              const normalizedRow = row.slice(0, columns.length).map((cell) => String(cell || ''))
              while (normalizedRow.length < columns.length) normalizedRow.push('')
              return normalizedRow
            })

            const existingTable = dynamicTables[data.tool_call_id]

            if (existingTable?.priority == null) tableOrderCounter += 1

            const arrivalOrder = existingTable?.priority ?? tableOrderCounter

            dynamicTables = {
              ...dynamicTables,
              [data.tool_call_id]: {
                title: tableTitle,
                columns,
                data: tableData,
                max_rows: maxRows,
                priority: arrivalOrder,
                id: mergedPayload.id || data.tool_call_id,
              },
            }
            observer.next(themisAnalysisPartialUpdate(reportId, { dynamicTables }))
          } catch {
            dynamicTables = {
              ...dynamicTables,
              [data.tool_call_id]: {
                title: 'Table Unavailable',
                columns: ['Error'],
                data: [['Invalid table data provided']],
                max_rows: 1,
                priority: 0,
                id: data.tool_call_id,
                isError: true,
              },
            }
            observer.next(themisAnalysisPartialUpdate(reportId, { dynamicTables }))
          }
        }
        return
      }

      if (data.event_type === 'tool_call_retry') {
        const storedArgs = toolArgsMap.get(data.tool_call_id) || {}
        const sourceData = generateSourceData(data.tool_call_id, data.tool_name, storedArgs, null)
        if (sourceData) {
          sourceData.status = 'retrying'
          sourceData.description += ' (Retrying...)'
          appendOrUpdateSource(data.tool_call_id, sourceData)
        }
        return
      }

      if (data.event_type === 'final_result' && data.structured_output) {
        if (textBuffer.trim()) {
          appendAiMessage(unescapeMarkdown(textBuffer))
          textBuffer = ''
        }
        const unescapedOutput = {}
        Object.keys(data.structured_output).forEach((field) => {
          unescapedOutput[field] = unescapeMarkdown(data.structured_output[field])
        })
        analysisData = unescapedOutput
        fieldLoadingStates = {
          sender_analysis: false,
          body_analysis: false,
          links_analysis: false,
          attachments_analysis: false,
          relays_analysis: false,
          label_explanation: false,
        }
        observer.next(
          themisAnalysisPartialUpdate(reportId, {
            data: analysisData,
            loading: false,
            isComplete: true,
            fieldLoadingStates,
          })
        )
        return
      }

      if (data.event_type === 'final_result_part' && data.structured_output) {
        if (tasks) {
          const updated = { ...tasks }
          Object.keys(updated).forEach((taskId) => {
            if (updated[taskId].status === 'RUNNING') {
              updated[taskId] = { ...updated[taskId], status: 'SUCCESS' }
            }
          })
          tasks = updated
        }

        const newData = { ...analysisData }
        Object.keys(data.structured_output).forEach((field) => {
          const deltaText = data.structured_output[field]
          if (deltaText) {
            newData[field] = (newData[field] || '') + unescapeMarkdown(deltaText)
          }
        })
        analysisData = newData

        const updatedFieldLoadingStates = { ...fieldLoadingStates }
        Object.keys(data.structured_output).forEach((field) => {
          if (data.structured_output[field]) {
            updatedFieldLoadingStates[field] = false
          }
        })
        fieldLoadingStates = updatedFieldLoadingStates

        observer.next(
          themisAnalysisPartialUpdate(reportId, {
            data: analysisData,
            tasks,
            fieldLoadingStates,
          })
        )
      }
    } catch {} // eslint-disable-line no-empty
  }

  const run = async () => {
    try {
      const response = await api.resolutionCenter.getStreamingAnalysis(reportId, reportType, {
        signal: controller.signal,
      })

      if (response?.status === 402) {
        observer.next(themisAnalysisFailure(reportId, 'Insufficient credits'))
        // The 402 proves the balance is 0; refetch so the counter is not left stale.
        observer.next(GET_BILLING_STATUS_ASYNC.initiate())
        observer.complete()
        return
      }

      if (!response?.ok) throw new Error(`HTTP error! status: ${response?.status}`)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop()

        lines.forEach(processLine)
      }

      if (textBuffer.trim()) {
        appendAiMessage(unescapeMarkdown(textBuffer))
        textBuffer = ''
      }

      observer.next(themisAnalysisSuccess(reportId))
      observer.complete()
    } catch (error) {
      if (controller.signal?.aborted) {
        observer.complete()
        return
      }
      observer.next(themisAnalysisFailure(reportId, error?.message || 'Failed to load analysis'))
      observer.complete()
    }
  }

  run()

  return () => {
    controller.abort()
  }
}

const themisAnalysisEpic = (action$, state$, { api }) =>
  action$.pipe(
    ofType(THEMIS_ANALYSIS_ASYNC.INITIATE),
    switchMap(({ payload: { reportId, reportType } }) => {
      // Every caller passes reportType on the action now; never send a
      // null/non-MAIL report_type to the token endpoint.
      if (reportType !== INCIDENT_TYPE.MAIL) {
        return EMPTY
      }
      return new Observable((observer) =>
        createThemisObserver(observer, reportId, reportType, api)
      ).pipe(
        takeUntil(
          action$.pipe(
            ofType(THEMIS_ANALYSIS_ASYNC.CANCEL),
            filter((a) => a.payload.reportId === reportId)
          )
        )
      )
    })
  )

export default combineEpics(
  fetchReportEpic,
  fetchCommunityInsightsEpic,
  fetchAssigneesEpic,
  changeReportStateEpic,
  updateElementsEpic,
  fetchElementsEpic,
  validateNewAffectedMailboxesSeen,
  fetchAffMailboxesCountsEpic,
  fetchScansFreemiumLimitEpic,
  changeAssigneeEpic,
  downloadOriginalEmailEpic,
  closeIncidentEpic,
  closeSafeChallengeIncidentEpic,
  releaseEncryptedIncidentEpic,
  keepEncryptedIncidentInQuarantine,
  emailSnapshotEpic,
  fetchReportElementsEpic,
  fetchPaginatedReportersEpic,
  fetchMSFTQuarantineReportEpic,
  releaseMSFTQuarantineReportEpic,
  updateMSFTQuarantineAssigneeEpic,
  themisAnalysisEpic
)
